import { Button, Chip, Input, IconButton, Dialog, DialogHeader, DialogBody, DialogFooter, Spinner } from '@material-tailwind/react';
import { UserCogIcon, EyeIcon, EyeOffIcon, CopyIcon, AlertTriangleIcon } from 'lucide-react';
import React, { useState } from 'react';
import { useSendResetPasswordEmail, useUpdateUserStatus, useRevealPassword, useForceResetPassword } from '../../../custom-hooks/use-users';
import { useConfirm } from '../../../custom-hooks/confirm-dialog';
import { Notify } from '../../../utils/functions';
import { useQueryClient } from 'react-query';
const AccountManagement = ({ customer }) => {
    const updateUserStatusMutation = useUpdateUserStatus();
    const sendResetPasswordEmailMutation = useSendResetPasswordEmail();
    const { confirm, ConfirmationDialog } = useConfirm();

    const handleUpdateUserStatus = async () => {
        const confirmed = await confirm({
            title: `${customer.isActive ? 'Disable' : 'Enable'} Account`,
            message: `Are you sure you want to ${customer.isActive ? 'disable' : 'enable'} ${customer.name}'s account?`,
            confirmText: customer.isActive ? 'Disable' : 'Enable',
            confirmColor: customer.isActive ? 'red' : 'green'
        });

        if (!confirmed) return;

        updateUserStatusMutation.mutate({ userId: customer._id, status: !customer.isActive });
    }

    const handleSendResetPasswordEmail = async () => {
        const confirmed = await confirm({
            title: 'Send Reset Password Email',
            message: `Send a reset password email to ${customer.email}?`,
            confirmText: 'Send Email',
            confirmColor: 'blue'
        });

        if (!confirmed) return;

        sendResetPasswordEmailMutation.mutate({ email: customer.email, userId: customer._id });
    }

    const revealPasswordMutation = useRevealPassword();
    const forceResetPasswordMutation = useForceResetPassword();
    const [fetchedPassword, setFetchedPassword] = useState("");
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const [isForceResetModalOpen, setIsForceResetModalOpen] = useState(false);

    const queryClient = useQueryClient();

    const handleTogglePasswordVisibility = async () => {
        if (!fetchedPassword) {
            // Need to fetch it first
            try {
                const res = await revealPasswordMutation.mutateAsync({ userId: customer._id });
                if (res?.data?.password) {
                    setFetchedPassword(res.data.password);
                    setIsPasswordVisible(true);
                }
            } catch (error) {
                // If it fails (e.g. 400 password changed by user), we should refresh the user data so UI updates
                queryClient.invalidateQueries(['user', customer._id]);
                queryClient.invalidateQueries('users');
            }
        } else {
            // Already fetched, just toggle visibility
            setIsPasswordVisible(!isPasswordVisible);
        }
    };

    const handleForceResetPassword = () => {
        setIsForceResetModalOpen(true);
    };

    const confirmForceResetPassword = async () => {
        setIsForceResetModalOpen(false);
        const res = await forceResetPasswordMutation.mutateAsync({ userId: customer._id });
        if (res?.data?.generatedPassword) {
            setFetchedPassword(res.data.generatedPassword);
            setIsPasswordVisible(true);
            Notify("New password generated successfully", "success");
        }
    };

    return (
        <div className="border rounded-xl p-6">
            <div className="flex gap-4">
                <h4 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <UserCogIcon size={16} />
                    Account Management
                </h4>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex justify-between items-center gap-2 bg-gray-200 rounded-lg p-4">
                    <div className="flex gap-2">
                        <p className="text-md text-gray-600">Account Status</p>
                        <Chip color={customer.isActive ? "green" : "red"} size="sm" variant="ghost" value={customer.isActive ? "Active" : "Inactive"} className="rounded-full" />
                    </div>
                    <div className="flex flex-col gap-2">
                        <Button
                            variant="gradient"
                            color={customer.isActive ? "red" : "green"}
                            size="sm"
                            className="rounded-full"
                            loading={updateUserStatusMutation.isLoading}
                            onClick={handleUpdateUserStatus}>
                            {customer.isActive ? "Disable login" : "Enable login"}
                        </Button>
                    </div>
                </div>
                <div className="flex justify-between items-center gap-2 bg-gray-200 rounded-lg p-4">
                    <p className="text-md text-gray-600">Reset Password</p>
                    <div className="flex gap-2">
                        <Button variant="gradient" color="light-blue" size="sm" className="rounded-full" loading={sendResetPasswordEmailMutation.isLoading} onClick={handleSendResetPasswordEmail}>
                            Send Reset Password Email
                        </Button>
                    </div>
                </div>
                
                {/* Admin Password Management Block */}
                <div className="flex flex-col gap-4 bg-gray-200 rounded-lg p-4 md:col-span-2">
                    <div className="flex justify-between items-center">
                        <p className="text-md text-gray-600">Password Management</p>
                        <Button variant="gradient" color="black" size="sm" className="rounded-full" loading={forceResetPasswordMutation.isLoading} onClick={handleForceResetPassword}>
                            Force Reset Password
                        </Button>
                    </div>
                    {customer.passwordVault?.passwordSetBy === 'admin' ? (
                        <div className="flex items-center gap-2 mt-2">
                            <div className="w-64">
                                <Input
                                    type={isPasswordVisible ? "text" : "password"}
                                    disabled={!fetchedPassword}
                                    value={fetchedPassword || "********"}
                                    label="Customer Password"
                                    icon={
                                        isPasswordVisible ? (
                                            <EyeOffIcon className="cursor-pointer text-gray-600" size={16} onClick={handleTogglePasswordVisibility} />
                                        ) : (
                                            <EyeIcon className="cursor-pointer text-gray-600" size={16} onClick={handleTogglePasswordVisibility} />
                                        )
                                    }
                                    readOnly
                                    className="bg-white disabled:bg-gray-100"
                                />
                            </div>
                            {fetchedPassword && (
                                <IconButton variant="text" className="shrink-0" onClick={() => {
                                    navigator.clipboard.writeText(fetchedPassword);
                                    Notify("Copied to clipboard", "success");
                                }}>
                                    <CopyIcon className="text-gray-600" size={18} />
                                </IconButton>
                            )}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500 italic mt-2">Password was changed by the user and is securely hidden.</p>
                    )}
                </div>
            </div>
            <ConfirmationDialog />
            
            <Dialog open={isForceResetModalOpen} handler={() => setIsForceResetModalOpen(false)} size='sm' className='rounded-2xl border-none outline-none'>
                <DialogHeader className='flex items-center gap-2'>
                    <AlertTriangleIcon size={18} className='text-amber-700' />
                    Force Reset Password
                </DialogHeader>
                <DialogBody className='space-y-4 text-blue-gray-900'>
                    <p className='text-base text-blue-gray-800 font-medium'>
                        Are you sure you want to generate a new password for <span className="font-bold text-black">{customer.name}</span>?
                    </p>
                    <p className='text-sm font-medium text-red-700'>
                        The old password will no longer be usable and all previous active sessions may be impacted. Please proceed only if necessary.
                    </p>
                </DialogBody>
                <DialogFooter className='gap-2'>
                    <Button variant='text' color='blue-gray' className='focus:outline-none focus:ring-0' onClick={() => setIsForceResetModalOpen(false)} disabled={forceResetPasswordMutation.isLoading}>
                        Cancel
                    </Button>
                    <Button className='min-w-[120px] flex justify-center bg-black text-white shadow-none transition-all duration-200 hover:bg-zinc-800 hover:-translate-y-[1px] hover:shadow-md active:translate-y-0 active:bg-zinc-900 focus:outline-none focus:ring-0 disabled:opacity-60' onClick={confirmForceResetPassword} disabled={forceResetPasswordMutation.isLoading}>
                        {forceResetPasswordMutation.isLoading ? <Spinner className='h-4 w-4' /> : 'Generate New Password'}
                    </Button>
                </DialogFooter>
            </Dialog>
        </div>
    );
};

export default AccountManagement;